import React, { FC } from 'react';
import styles from './CreateUserForm.module.scss';
import PropTypes from 'prop-types';
import { Button, Input, Checkbox, TreeSelect, FileUploader, Select, ArrowRightLight } from 'esa-ui-library';
import {
	Form,
	PasswordIndicator,
	emailValidator,
	usernameValidator,
	passFairValidator,
	passStrongValidator,
	EMAIL_INVALID_MESSAGE,
	USERNAME_INVALID_MESSAGE,
} from '../../../..';
import { PropertyType, useStore, useValidator, getImageUrl, FileType } from '../../../../../common';

type ValueType = {
	label: string;
	value: string;
};

interface CreateUserFormProps {
	onSubmit: () => void;
}

const CreateUserForm: FC<CreateUserFormProps> = ({ onSubmit }): JSX.Element => {
	const [state, dispatch] = useStore();
	const {
		username: initialUsername,
		usernameInvalid,
		email: initialEmail,
		emailInvalid,
		password: initialPassword,
		passwordFair,
		passwordStrong,
		partners,
		imagePreview,
		activePartners,
		languages,
		activeLanguage,
		dateTimes,
		activeDateTime,
		errors,
		success,
	} = state;

	const [username, setUsername] = React.useState(initialUsername || '');
	const [email, setEmail] = React.useState(initialEmail || '');
	const [password, setPassword] = React.useState(initialPassword || '');

	React.useEffect(() => {
		setUsername(initialUsername || '');
	}, [initialUsername]);

	React.useEffect(() => {
		setEmail(initialEmail || '');
	}, [initialEmail]);

	React.useEffect(() => {
		setPassword(initialPassword || '');
	}, [initialPassword]);

	const allPartnersSelected = activePartners.some((p: PropertyType) => p.value === '0');

	const isPermissionsDiabled = [
		!!username,
		!usernameInvalid,
		!!email,
		!emailInvalid,
		!!password,
		!!activePartners.length,
		passwordStrong,
	].some((i) => !i);

	useValidator(email, emailValidator, 'EMAIL_INVALID');
	useValidator(username, usernameValidator, 'USERNAME_INVALID');
	useValidator(password, passFairValidator, 'PASS_FAIR');
	useValidator(password, passStrongValidator, 'PASS_STRONG');

	const onUsernameChange = (val: string) => {
		setUsername(val);
		dispatch('USERNAME', val);
	};

	const onEmailChange = (val: string) => {
		setEmail(val);
		dispatch('EMAIL', val);
	};

	const onPasswordChange = (val: string) => {
		setPassword(val);
		dispatch('PASS', val);
	};

	const onPartnersChange = (val: PropertyType[]) => {
		dispatch('PARTNERS_UPDATE', val);
	};

	const onAllPartnersChange = (val: boolean) => {
		const topLevel = { label: 'All partners', value: '0' };
		return onPartnersChange(val ? [topLevel] : []);
	};

	const onImagePreview = (url: FileType) => {
		dispatch('IMAGE_PREVIEW', url);
	};

	const onImageChange = async (val: File | null) => {
		dispatch('IMAGE_UPDATE', val);
		if (val) return getImageUrl(val, onImagePreview);
		dispatch('IMAGE_PREVIEW', null);
	};

	const onLanguageChange = (val: ValueType[]) => {
		dispatch('LANGUAGE', val);
	};

	const onDateTimeChange = (val: ValueType[]) => {
		dispatch('DATE_TIME', val);
	};

	return (
		<section className={styles.CreateUserForm}>
			<Form errors={errors} success={success}>
				<FileUploader
					image={imagePreview}
					placeholder='JPG, PNG or GIF. Max size 100kb'
					dataId='image-field'
					onChange={onImageChange}
				/>
				<Input
					type='text'
					label='Username'
					value={username}
					placeholder='Enter username'
					dataId='username-field'
					required
					onChange={onUsernameChange}
					error={usernameInvalid}
					errorMessage={USERNAME_INVALID_MESSAGE}
				/>
				<Input
					type='text'
					label='Email'
					value={email}
					placeholder='Enter email'
					dataId='email-field'
					required
					onChange={onEmailChange}
					error={emailInvalid}
					errorMessage={EMAIL_INVALID_MESSAGE}
				/>
				<Input
					type='password'
					label='Password'
					value={password}
					placeholder='Enter password'
					required
					dataId='password-field'
					onChange={onPasswordChange}
				/>
				{password && <PasswordIndicator fair={passwordFair} strong={passwordStrong} />}
				<Checkbox
					label='Select all partners'
					checked={allPartnersSelected}
					onChange={onAllPartnersChange}
					className={styles.AllPartners}
					dataId='select-all-partners'
				/>
				<TreeSelect
					label='Partners'
					options={partners}
					placeholder='Select partners'
					selected={activePartners}
					disabled={allPartnersSelected}
					required
					dataId='partners-field'
					onChange={onPartnersChange}
				/>
				<Select
					label='Default language'
					options={languages}
					placeholder='Select language'
					selected={activeLanguage}
					dataId='language-field'
					onChange={onLanguageChange}
				/>
				<Select
					label='Date/Time format'
					options={dateTimes}
					placeholder='Select date/time format'
					selected={activeDateTime}
					dataId='date-format-field'
					onChange={onDateTimeChange}
				/>
				<Button
					primary
					dataId='permission-button'
					disabled={isPermissionsDiabled}
					className={styles.DefinePermissions}
					onClick={onSubmit}
				>
					Define Permissions
					<ArrowRightLight className={styles.DefinePermissionsIcon} />
				</Button>
			</Form>
		</section>
	);
};

CreateUserForm.propTypes = {
	onSubmit: PropTypes.func.isRequired,
};

export { CreateUserForm };
